<template>
    <Dialog :open="open" @update:open="handleClose">
        <DialogContent class="sm:max-w-md">
            <DialogHeader>
                <DialogTitle>Delete Token</DialogTitle>
            </DialogHeader>

            <div class="space-y-4">
                <p class="text-sm">
                    Are you sure you want to delete the
                    <span class="font-semibold text-foreground">{{
                        token.name
                    }}</span>
                    token?
                </p>

                <div>
                    <Label for="confirm-text" class="text-foreground"
                        >Type "{{ token.name }}" to confirm</Label
                    >
                    <Input
                        id="confirm-text"
                        v-model="confirmText"
                        :placeholder="token.name"
                        class="mt-2"
                    />
                </div>

                <div class="flex items-center gap-3 bg-new-amber-100 p-3">
                    <TriangleAlert
                        class="size-3.5 shrink-0 text-new-amber-600"
                    />
                    <p class="text-xs text-new-amber-600">
                        This action will permanently delete this API token
                        and revoke all access. This cannot be undone.
                    </p>
                </div>
            </div>

            <DialogFooter class="mt-6">
                <Button variant="outline" @click="handleClose">
                    Cancel
                </Button>
                <Button
                    variant="destructive"
                    :disabled="confirmText !== token.name || loading"
                    @click="handleDelete"
                >
                    Delete token
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script lang="ts" setup>
import { ref, PropType } from 'vue';
import IToken from '@/types/Plugin/IToken';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TriangleAlert } from 'lucide-vue-next';

const props = defineProps({
    open: {
        type: Boolean,
        required: true,
    },
    token: {
        type: Object as PropType<IToken>,
        required: true,
    },
    loading: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['close', 'remove', 'update:open']);

const confirmText = ref('');

const handleClose = () => {
    confirmText.value = '';
    emit('close');
    emit('update:open', false);
};

const handleDelete = () => {
    if (confirmText.value === props.token.name) {
        emit('remove');
    }
};
</script>
